import { ColumnSource, RowSource, CellModel, RowModel } from "@omegagrid/grid";
import { ColorTypes, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, FormulaEditor, FormulaEditorAdapter, VariableSource } from "@reporting/rp-app-host";
import { IndicatorDefinition } from '@reporting/rp-types/definition';
import { EntityAccessType } from "@reporting/rp-types/users";

class CompFormulaEditorAdapter extends FormulaEditorAdapter<FormulaEditor> {

	constructor(
		private origFormula: string,
		private onFormulaSave: (newFormula: string) => void
	) {	super() }

	async getVariables(): Promise<VariableSource[]> {
		const indicators = await rp.ms('definition').get<IndicatorDefinition[]>(`itemIndicators/C`);
		return indicators.map(indicator => ({
			variable: `${indicator.balance ? 'BALANCE_' : ''}${indicator.type}_${indicator.id_indicator}`,
			description: indicator.description,
		}));
	}

	async getContent(): Promise<string> {
		return this.origFormula;
	}

	async save(formula?: string): Promise<void|string> {
		this.onFormulaSave(formula);
	}

	getOptions() {
		return this.createOptions({
			closeAfterSave: true,
			toolbar: true,
			variablesGrid: true
		});
	}
}

export class ItemCompIndicatorGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private editedRow: RowModel;

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 3,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'edit',
				title: msg('Edit'),
				icon: 'pen',
				color: ColorTypes.Blue
			}, {
				action: 'access',
				title: msg('Access'),
				icon: 'shield-check',
				color: ColorTypes.Gray
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 't',
			filter: false,
			e: true,
			w: 60
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			e: true,
			w: 180
		}, {
			data: 'formula',
			header: msg('Formula'),
			t: 't',
			e: true,
			w: 400
		}, {
			data: 'dec_num',
			header: msg('Decimal digit count'),
			t: 'n',
			filter: false,
			e: true,
			w: 140
		}, {
			data: 'um',
			header: msg('Units'),
			t: 't',
			filter: false,
			e: true,
			w: 75
		}, {
			data: 'remark',
			header: msg('Remark'),
			t: 't',
			filter: false,
			e: true,
			w: 200
		}, {
			data: 'id_indicator',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const indicators = await rp.ms('definition').get<IndicatorDefinition[]>(`itemIndicators/C`, {
			order: [['ord_num', 'asc'], ['description', 'asc']]
		});
		return indicators.map(data => ({ data }));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			pageSize: null,
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index, type: 'COMP'}, row.data));
		await rp.ms('definition').post('itemIndicators', data);
	}

	async onAction(action: string, cell: CellModel) {
		const data = cell?.row?.data as IndicatorDefinition;
		if (!data?.id_indicator) return;
		switch (action) {
			case 'access':
				rp.host.open(`users-entityUsers-${EntityAccessType.ItemIndicator}-${data.id_indicator}`, { title: `${msg('Access')}: ${data.description}` });
				break;
			case 'edit':
				this.editedRow = cell.row;
				rp.host.openModalSidebar({
					component: this.getFormulaEditorComponent(data.formula),
					caption: `#${data.id_indicator} ${data.description}`,
					width: 500,
				})
				break;
		}
	}

	getFormulaEditorComponent(origFormula: string): HTMLElement {
		const component = dom.createElement<FormulaEditor>('rp-editor-formula');
		component.adapter = new CompFormulaEditorAdapter(origFormula, this.setIndicatorFormula.bind(this));
		return component;
	}

	setIndicatorFormula(newFormula: string) {
		const r = this.editedRow.index;
		const c = this.editedRow.cells.findIndex(cell => cell.column.data == 'formula');
		if (c < 0) return;
		this.editor.setCellValue(r, c, newFormula);
	}
}
